// ambience — looping background beds (shop / battle). Separate from
// the one-shot sfx engine: a single Howl plays at a time, loops, sits
// at a low base volume, and crossfades when the scene changes.
//
// Respects the same master volume / mute as sfx (Howler's global
// volume multiplies the bed's base), so the settings slider and mute
// toggle govern it too. Uses html5 streaming since the beds are long
// (~30s / ~480KB) — no need to decode the whole buffer into memory.

import { Howl } from 'howler'
import { getSoundSettings, masterGain, subscribeSoundSettings } from './soundSettings'

export type AmbienceTrack = 'shop' | 'battle'

/** Selectable shop ambience tracks — surfaced in the options menu.
 *  `id` is what persists in settings; 'off' silences the shop bed. */
export interface ShopBed {
  id: string
  label: string
  file: string
}

export const SHOP_BEDS: ShopBed[] = [
  // Full composed theme — the default shop music.
  { id: 'moonlit', label: 'Moonlit Yubiwa Garden', file: 'ambience-shop-moonlit.mp3' },
  // Short ambient beds.
  { id: 'tranquility', label: 'Tranquility', file: 'ambience-shop-tranquility.mp3' },
  { id: 'teahouse', label: 'Teahouse', file: 'ambience-shop-teahouse.mp3' },
  { id: 'sakura', label: 'Sakura Drift', file: 'ambience-shop-sakura.mp3' },
]

export const BATTLE_BEDS: ShopBed[] = [
  // Full composed theme — the default battle music.
  { id: 'moonkoto', label: 'Moon Over Koto', file: 'ambience-battle-moon.mp3' },
  // Short ambient beds.
  { id: 'resolve', label: 'Resolve', file: 'ambience-battle-resolve.mp3' },
  { id: 'onslaught', label: 'Onslaught', file: 'ambience-battle-onslaught.mp3' },
  { id: 'valor', label: 'Valor', file: 'ambience-battle-valor.mp3' },
]

/** Base gain for beds — deliberately quiet so they sit under sfx. */
const BED_VOLUME = 0.22
const FADE_MS = 800

interface ActiveBed {
  track: AmbienceTrack
  file: string
  howl: Howl
}

// Howls cached by FILE (shop track selection swaps files).
const cache = new Map<string, Howl>()
let active: ActiveBed | null = null
// The scene the caller last requested — tracked separately from
// `active` so an 'off' shop bed (active=null) still knows the scene
// is "shop" when the user later picks a track.
let wantedTrack: AmbienceTrack | null = null

function howlFor(file: string): Howl {
  let howl = cache.get(file)
  if (!howl) {
    howl = new Howl({
      src: [`${import.meta.env.BASE_URL}sfx/${file}`],
      loop: true,
      html5: true, // stream the long file
      volume: 0, // faded in by setAmbience
      preload: true,
    })
    cache.set(file, howl)
  }
  return howl
}

/** Resolve the file for a track from the selected bed in settings.
 *  'off' → null = silent. */
function fileForTrack(track: AmbienceTrack): string | null {
  const { beds, id } =
    track === 'battle'
      ? { beds: BATTLE_BEDS, id: getSoundSettings().battleBed }
      : { beds: SHOP_BEDS, id: getSoundSettings().shopBed }
  if (id === 'off') return null
  return (beds.find((b) => b.id === id) ?? beds[0]!).file
}

function fadeOutActive(): void {
  if (!active) return
  const prev = active.howl
  prev.fade(prev.volume(), 0, FADE_MS)
  window.setTimeout(() => prev.stop(), FADE_MS + 30)
  active = null
}

/** Switch the looping bed. `null` fades out whatever's playing. Safe
 *  to call repeatedly with the same track. */
export function setAmbience(track: AmbienceTrack | null): void {
  if (typeof window === 'undefined') return
  wantedTrack = track

  if (!track) {
    fadeOutActive()
    return
  }

  const file = fileForTrack(track)
  // Same track AND same resolved file → no-op (already playing it).
  if (active?.track === track && active.file === file) return
  // Shop set to 'off' → just silence whatever's playing.
  if (file === null) {
    fadeOutActive()
    return
  }

  fadeOutActive()
  const howl = howlFor(file)
  // Mute is handled by Howler's global volume (masterGain → 0); we
  // still fade the bed's own volume up to its base so unmuting later
  // reveals it at the right level.
  howl.volume(0)
  if (!howl.playing()) howl.play()
  howl.fade(0, BED_VOLUME, FADE_MS)
  active = { track, file, howl }
}

// Live track swap: when the user changes the selected bed for the
// CURRENT scene in the options, re-resolve and crossfade (covers
// off→track, track→track, track→off) for both shop and battle.
if (typeof window !== 'undefined') {
  subscribeSoundSettings(() => {
    if (!wantedTrack) return
    const file = fileForTrack(wantedTrack)
    if ((active?.file ?? null) === file) return
    // Re-enter the scene so the new file resolves + crossfades. Reset
    // `active.file` first so the same-track no-op guard doesn't skip.
    if (active) active = { ...active, file: '__swap__' }
    setAmbience(wantedTrack)
  })
}

/** Currently-playing bed track, or null. */
export function currentAmbience(): AmbienceTrack | null {
  return active?.track ?? null
}

/** True if the bed should be audible given mute state — for UI. */
export function ambienceAudible(): boolean {
  return masterGain() > 0
}
